import React, { useEffect } from 'react'

import { LDProvider, useLDClient } from 'launchdarkly-react-client-sdk'

interface LaunchDarklyProps {
  onReady: () => void
  children: React.ReactNode
  userDetails: {
    id: string
    companyId: string
    parentCompanyId?: string
    email: string
  }
}

export default function LaunchDarkly({ onReady, children, userDetails }: LaunchDarklyProps): JSX.Element {
  const deploymentStage = process.env.DEPLOYMENT_STAGE
  let environment
  if (deploymentStage === 'local' || deploymentStage.startsWith('dev')) {
    environment = 'dev'
  } else if (deploymentStage === 'master') {
    environment = 'production'
  } else {
    environment = deploymentStage
  }

  const context = {
    kind: 'multi',
    user: {
      key: userDetails.id,
      companyId: userDetails.companyId,
      email: userDetails.email,
      parentCompanyId: userDetails.parentCompanyId,
      environment
    },
    companyId: {
      key: userDetails.companyId,
      parentCompanyId: userDetails.parentCompanyId,
      environment
    }
  }

  if (process.env.LAUNCHDARKLY_CLIENT_ID) {
    return (
      <LDProvider clientSideID={process.env.LAUNCHDARKLY_CLIENT_ID} context={context}>
        <LaunchDarklyReadyListener onReady={onReady}>{children}</LaunchDarklyReadyListener>
      </LDProvider>
    )
  } else {
    return <>{children}</>
  }
}
function LaunchDarklyReadyListener({ onReady, children }): JSX.Element {
  const ldClient = useLDClient()

  // If LaunchDarkly doesn't load within 5 seconds, we still want to render the app
  useEffect(() => {
    const timeoutMs = 5000
    let timeout: NodeJS.Timeout | null = null

    if (ldClient) {
      // Start timeout
      timeout = setTimeout(onReady, timeoutMs)
      // Wait for LaunchDarkly or timeout, whichever comes first
      ldClient.waitForInitialization(5).then(() => {
        onReady()
      })
    } else {
      // If ldClient is not available, just use the timeout
      timeout = setTimeout(onReady, timeoutMs)
    }

    // Cleanup timeout if component unmounts before timeout fires
    return () => {
      if (timeout) {
        clearTimeout(timeout)
      }
    }
  }, [ldClient, onReady])

  return <>{children}</>
}
